import { useState, useCallback, useEffect, RefObject } from "react";

interface CellPosition {
  row: number;
  col: number;
}

interface UseGridNavigationOptions {
  rowCount: number;
  colCount: number;
  onCellSelect?: (position: CellPosition) => void;
  onCellEdit?: (position: CellPosition) => void;
  onCellSave?: () => void;
  onCellCancel?: () => void;
  onCellDelete?: (position: CellPosition) => void;
  containerRef: RefObject<HTMLElement>;
}

export function useGridNavigation({
  rowCount,
  colCount,
  onCellSelect,
  onCellEdit,
  onCellSave,
  onCellCancel,
  onCellDelete,
  containerRef,
}: UseGridNavigationOptions) {
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [selectionRange, setSelectionRange] = useState<{
    start: CellPosition;
    end: CellPosition;
  } | null>(null);

  const isEditing = editingCell !== null;

  const selectCell = useCallback(
    (position: CellPosition, startEdit = false) => {
      if (position.row < 0 || position.row >= rowCount) return;
      if (position.col < 0 || position.col >= colCount) return;

      setSelectedCell(position);
      setSelectionRange(null);
      onCellSelect?.(position);

      if (startEdit) {
        setEditingCell(position);
        onCellEdit?.(position);
      }
    },
    [rowCount, colCount, onCellSelect, onCellEdit]
  );

  const startEditing = useCallback(() => {
    if (selectedCell && !editingCell) {
      setEditingCell(selectedCell);
      onCellEdit?.(selectedCell);
    }
  }, [selectedCell, editingCell, onCellEdit]);

  const stopEditing = useCallback(
    (save = true) => {
      if (editingCell) {
        if (save) {
          onCellSave?.();
        } else {
          onCellCancel?.();
        }
        setEditingCell(null);
      }
    },
    [editingCell, onCellSave, onCellCancel]
  );

  const moveSelection = useCallback(
    (deltaRow: number, deltaCol: number, wrap = false) => {
      if (!selectedCell) {
        selectCell({ row: 0, col: 0 });
        return;
      }

      let newRow = selectedCell.row + deltaRow;
      let newCol = selectedCell.col + deltaCol;

      if (wrap) {
        // Wrap to next/previous row
        if (newCol >= colCount) {
          newCol = 0;
          newRow++;
        } else if (newCol < 0) {
          newCol = colCount - 1;
          newRow--;
        }
      }

      // Clamp to bounds
      newRow = Math.max(0, Math.min(rowCount - 1, newRow));
      newCol = Math.max(0, Math.min(colCount - 1, newCol));

      if (newRow !== selectedCell.row || newCol !== selectedCell.col) {
        if (editingCell) {
          stopEditing(true);
        }
        selectCell({ row: newRow, col: newCol });
      }
    },
    [selectedCell, editingCell, rowCount, colCount, selectCell, stopEditing]
  );

  const extendSelection = useCallback(
    (deltaRow: number, deltaCol: number) => {
      if (!selectedCell) return;

      const start = selectionRange?.start || selectedCell;
      const currentEnd = selectionRange?.end || selectedCell;

      const newEnd = {
        row: Math.max(0, Math.min(rowCount - 1, currentEnd.row + deltaRow)),
        col: Math.max(0, Math.min(colCount - 1, currentEnd.col + deltaCol)),
      };

      setSelectionRange({ start, end: newEnd });
    },
    [selectedCell, selectionRange, rowCount, colCount]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't handle if focus is in an input that's not our grid
      const target = event.target as HTMLElement;
      const isInGrid =
        containerRef.current?.contains(target) ||
        target.closest("[data-grid-cell]");

      if (!isInGrid && !editingCell) return;

      // Allow certain keys when editing
      if (editingCell) {
        switch (event.key) {
          case "Escape":
            event.preventDefault();
            stopEditing(false);
            break;
          case "Enter":
            if (!event.shiftKey) {
              event.preventDefault();
              stopEditing(true);
              moveSelection(1, 0);
            }
            break;
          case "Tab":
            event.preventDefault();
            stopEditing(true);
            moveSelection(0, event.shiftKey ? -1 : 1, true);
            break;
        }
        return;
      }

      // Navigation when not editing
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          if (event.shiftKey) {
            extendSelection(-1, 0);
          } else {
            moveSelection(-1, 0);
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (event.shiftKey) {
            extendSelection(1, 0);
          } else {
            moveSelection(1, 0);
          }
          break;
        case "ArrowLeft":
          event.preventDefault();
          if (event.shiftKey) {
            extendSelection(0, -1);
          } else {
            moveSelection(0, -1);
          }
          break;
        case "ArrowRight":
          event.preventDefault();
          if (event.shiftKey) {
            extendSelection(0, 1);
          } else {
            moveSelection(0, 1);
          }
          break;
        case "Tab":
          event.preventDefault();
          moveSelection(0, event.shiftKey ? -1 : 1, true);
          break;
        case "Enter":
          event.preventDefault();
          if (selectedCell) {
            startEditing();
          }
          break;
        case "F2":
          event.preventDefault();
          if (selectedCell) {
            startEditing();
          }
          break;
        case "Escape":
          event.preventDefault();
          setSelectedCell(null);
          setSelectionRange(null);
          break;
        case "Delete":
        case "Backspace":
          if (selectedCell && !editingCell) {
            event.preventDefault();
            onCellDelete?.(selectedCell);
          }
          break;
        case "Home":
          event.preventDefault();
          if (event.ctrlKey || event.metaKey) {
            selectCell({ row: 0, col: 0 });
          } else {
            selectCell({ row: selectedCell?.row || 0, col: 0 });
          }
          break;
        case "End":
          event.preventDefault();
          if (event.ctrlKey || event.metaKey) {
            selectCell({ row: rowCount - 1, col: colCount - 1 });
          } else {
            selectCell({ row: selectedCell?.row || 0, col: colCount - 1 });
          }
          break;
        default:
          // Start editing on any printable character
          if (
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            selectedCell
          ) {
            startEditing();
          }
      }
    },
    [
      containerRef,
      editingCell,
      selectedCell,
      rowCount,
      colCount,
      moveSelection,
      extendSelection,
      selectCell,
      startEditing,
      stopEditing,
      onCellDelete,
    ]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const isSelected = useCallback(
    (row: number, col: number) => {
      if (selectedCell?.row === row && selectedCell?.col === col) return true;

      if (selectionRange) {
        const minRow = Math.min(selectionRange.start.row, selectionRange.end.row);
        const maxRow = Math.max(selectionRange.start.row, selectionRange.end.row);
        const minCol = Math.min(selectionRange.start.col, selectionRange.end.col);
        const maxCol = Math.max(selectionRange.start.col, selectionRange.end.col);

        return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
      }

      return false;
    },
    [selectedCell, selectionRange]
  );

  const isEditingCell = useCallback(
    (row: number, col: number) => {
      return editingCell?.row === row && editingCell?.col === col;
    },
    [editingCell]
  );

  return {
    selectedCell,
    editingCell,
    selectionRange,
    isEditing,
    selectCell,
    startEditing,
    stopEditing,
    moveSelection,
    isSelected,
    isEditingCell,
  };
}
